import { assertUnreachable, isNotDefined } from '@sgme/fp';

import type { StrategyTypeName } from '@/api/option/requestOptionRfs.ts';
import type { LegId, MultipassLegId, OptionProductName } from '@/store/option/state.ts';
import type { ISODate } from '@/types/date.ts';

export function toRfsDate(date: ISODate | undefined): Date | undefined {
  if (isNotDefined(date)) {
    return undefined;
  }
  return new Date(date);
}

export function mapProductNameToStrategyType(productName: OptionProductName, legsNumber: number): StrategyTypeName {
  if ((productName === 'Vanilla' || productName === 'Trigger') && legsNumber > 1) {
    return 'Multileg';
  }

  switch (productName) {
    case 'Vanilla':
      return 'Vanilla';
    case 'Trigger':
      return 'Multileg';
    case 'Binary':
      return 'Multileg';
    case 'Straddle':
      return 'Straddle';
    case 'Strangle':
      return 'Strangle';
    case 'RiskReversal':
      return 'RiskReversal';

    default:
      return assertUnreachable(productName, `Unknown product name: ${productName}`);
  }
}

/**
 * To be iso with FX.
 * This is probably OK to just return legId here but we decided that it was better not
 * to take any risk here.
 */
export function toMultipassLegId(legId: LegId, productName: OptionProductName): MultipassLegId {
  const { tileId, legNumber } = parseLegId(legId);

  switch (productName) {
    case 'RiskReversal':
    case 'Strangle':
    case 'Straddle': {
      const way = legNumber === 0 ? 'call' : 'put';
      return `${tileId}/0/0/${way}`;
    }
    case 'Vanilla':
    case 'Trigger':
    case 'Binary':
      return `${tileId}/0/${legNumber}`;
    default:
      assertUnreachable(productName, `Unknown product ${productName}`);
  }
}

export function parseLegId(legId: LegId): { tileId: string; legNumber: number } {
  const [tileId, legNumberString] = legId.split('/');
  if (isNotDefined(tileId)) {
    throw new Error('Invalid leg id for leg id');
  }

  const legNumber = Number.parseInt(legNumberString ?? '', 10);
  if (Number.isNaN(legNumber)) {
    throw new Error('Invalid leg id for leg id');
  }

  return {
    tileId,
    legNumber,
  };
}
